import { Worker } from 'node:worker_threads';
import { pathToFileURL } from 'node:url';
import { getFileBrowserActivitySharedBuffer } from '../../../../files/browser/in-flight.js';
import { isCliImmediateShutdownRequested } from '../../../../runtime/cli-process-interrupt.js';
import { CliSqliteInterrupted } from '../../../../sqlite/sqlite-errors.js';
import {
  handleWorkerOutMessage,
  pendingRequests,
  rejectAllPendingRequests,
  waitForWorkerReady,
} from '../client-state.js';
import {
  ensureInProcessBridge,
  getInProcessBridge,
} from '../in-process-bridge.js';
import type { SymbolIndexWorkerInMessage, SymbolIndexWorkerOutMessage } from '../protocol.js';
import {
  getTerminatePromise,
  getWorker,
  resolveWorkerScriptPath,
  setWorker,
  shouldUseInProcessWorker,
} from './state.js';

export function ensureWorkerStarted(): void {
  if (isCliImmediateShutdownRequested() || getTerminatePromise() != null) return;
  if (getWorker() != null || getInProcessBridge() != null) return;

  if (shouldUseInProcessWorker()) {
    void ensureInProcessBridge();
    return;
  }

  const scriptPath = resolveWorkerScriptPath();
  const nextWorker = new Worker(pathToFileURL(scriptPath), {
    type: 'module',
    workerData: { fileBrowserActivityBuffer: getFileBrowserActivitySharedBuffer() },
  } as import('node:worker_threads').WorkerOptions);
  nextWorker.on('message', handleWorkerOutMessage);
  nextWorker.on('error', (err) => {
    if (isCliImmediateShutdownRequested()) {
      rejectAllPendingRequests(new CliSqliteInterrupted());
      return;
    }
    rejectAllPendingRequests(err);
  });
  setWorker(nextWorker);
}

export async function ensureWorkerReadyForRequests(): Promise<void> {
  if (shouldUseInProcessWorker()) {
    await ensureInProcessBridge();
    return;
  }
  await waitForWorkerReady();
}

export function postToWorker(message: SymbolIndexWorkerInMessage): void {
  if (isCliImmediateShutdownRequested() || getTerminatePromise() != null) return;
  ensureWorkerStarted();
  const activeWorker = getWorker();
  if (activeWorker != null) {
    activeWorker.postMessage(message);
    return;
  }
  const bridge = getInProcessBridge();
  if (bridge != null) {
    bridge.postMessage(message);
    return;
  }
  if (shouldUseInProcessWorker()) {
    void ensureInProcessBridge().then((nextBridge) => nextBridge.postMessage(message));
  }
}

export async function requestWorker<T extends SymbolIndexWorkerOutMessage>(
  message: SymbolIndexWorkerInMessage & { requestId: string },
): Promise<T> {
  if (isCliImmediateShutdownRequested()) {
    throw new CliSqliteInterrupted();
  }
  ensureWorkerStarted();
  await ensureWorkerReadyForRequests();
  return new Promise<T>((resolve, reject) => {
    pendingRequests.set(message.requestId, {
      resolve: (value) => resolve(value as T),
      reject,
    });
    postToWorker(message);
  });
}
